import React from 'react';
import { Calendar, Clock, FileText, Hash, Check, Copy, Share2 } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

interface BookAuditTrailSectionProps {
  book: Book;
  copiedId: boolean;
  copiedUrl: boolean;
  onCopyRecordId: () => void;
  onCopyVerificationUrl: () => void;
}

export const BookAuditTrailSection: React.FC<BookAuditTrailSectionProps> = ({
  book,
  copiedId,
  copiedUrl,
  onCopyRecordId,
  onCopyVerificationUrl,
}) => {
  // Format date helper
  const formatTimestamp = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      return new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      }).format(d);
    } catch {
      return dateString;
    }
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const now = Date.now();
      const then = new Date(dateString).getTime();
      const diffSec = Math.floor((now - then) / 1000);
      if (diffSec < 60) return 'Baru saja';
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin} menit yang lalu`;
      const diffHour = Math.floor(diffMin / 60);
      if (diffHour < 24) return `${diffHour} jam yang lalu`;
      const diffDay = Math.floor(diffHour / 24);
      if (diffDay < 30) return `${diffDay} hari yang lalu`;
      return `${Math.floor(diffDay / 30)} bulan yang lalu`;
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 shadow-2xs space-y-2.5">
      <div className="flex items-center gap-1.5 font-bold text-slate-800 text-xs uppercase tracking-wide">
        <Calendar className="w-4 h-4 text-indigo-600" />
        <span>Waktu Registrasi & Jejak Audit Digital</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        {/* Created At */}
        <div className="p-2.5 rounded-lg bg-indigo-50/50 border border-indigo-100/80 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-indigo-800 uppercase flex items-center gap-1">
              <Clock className="w-3 h-3 text-indigo-600" />
              <span>Waktu Pendaftaran Naskah</span>
            </span>
            <span className="text-[10px] font-semibold text-indigo-600 bg-white px-2 py-0.5 rounded-full border border-indigo-200/70">
              {formatRelativeTime(book.createdAt)}
            </span>
          </div>
          <p className="font-semibold text-slate-900 text-xs">
            {formatTimestamp(book.createdAt)}
          </p>
          <p className="text-[10px] text-slate-500 font-mono">
            ISO: {book.createdAt}
          </p>
        </div>

        {/* Updated At */}
        <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200/70 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-slate-700 uppercase flex items-center gap-1">
              <FileText className="w-3 h-3 text-slate-500" />
              <span>Pembaruan Terakhir Data</span>
            </span>
            <span className="text-[10px] font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">
              {formatRelativeTime(book.updatedAt)}
            </span>
          </div>
          <p className="font-semibold text-slate-900 text-xs">
            {formatTimestamp(book.updatedAt)}
          </p>
          <p className="text-[10px] text-slate-500 font-mono">
            ISO: {book.updatedAt}
          </p>
        </div>
      </div>

      {/* Digital Stamp Hash & Record Integrity */}
      <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-2">
          <Hash className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <span className="text-slate-600">
            Unique Book Record ID:
          </span>
          <code className="px-1.5 py-0.5 rounded bg-white border border-slate-200 font-mono text-[10px] text-slate-800">
            {book.id}
          </code>
        </div>

        <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={onCopyRecordId}
            className="flex-1 sm:flex-none px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 border border-slate-200 text-[10px] font-medium flex items-center justify-center gap-1 transition cursor-pointer"
          >
            {copiedId ? (
              <>
                <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                <span className="text-emerald-700 font-semibold">Tersalin</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3 text-slate-400 shrink-0" />
                <span>Salin ID</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCopyVerificationUrl}
            className="flex-1 sm:flex-none px-2.5 py-1.5 rounded-lg bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 border border-slate-200 text-[10px] font-medium flex items-center justify-center gap-1 transition cursor-pointer"
          >
            {copiedUrl ? (
              <>
                <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                <span className="text-emerald-700 font-semibold">URL Tersalin</span>
              </>
            ) : (
              <>
                <Share2 className="w-3 h-3 text-slate-400 shrink-0" />
                <span className="hidden sm:inline">Salin Link Verifikasi</span>
                <span className="sm:hidden">Salin Link</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
